import { api } from './api';
import { getApiUrl } from '@/config/api.config';

export type HealthStatus = 'healthy' | 'backend_down' | 'database_down' | 'network_error';

interface HealthCheckResult {
  status: HealthStatus;
  message?: string;
}

class HealthCheckService {
  private checkInterval: NodeJS.Timeout | null = null;
  private listeners: Set<(status: HealthStatus) => void> = new Set();
  private lastStatus: HealthStatus = 'healthy';

  /**
   * Check backend and database health
   */
  async checkHealth(): Promise<HealthCheckResult> {
    try {
      const apiUrl = getApiUrl();
      console.log('🏥 [HealthCheck] Starting health check...');
      console.log('🏥 [HealthCheck] API URL:', apiUrl);
      console.log('🏥 [HealthCheck] navigator.onLine:', navigator.onLine);

      // Note: navigator.onLine can be unreliable, so we'll try fetch regardless
      if (!navigator.onLine) {
        console.log('⚠️ [HealthCheck] navigator.onLine is false, but attempting fetch anyway...');
      }

      // Try to reach backend health endpoint
      // Using raw fetch here instead of api client because:
      // 1. Health check needs custom 5s timeout (shorter than default)
      // 2. Should not trigger auth token refresh on failure
      // 3. Needs to work even when auth system is down
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout

      const healthUrl = `${apiUrl}/health`;
      console.log('🏥 [HealthCheck] Fetching:', healthUrl);

      try {
        const response = await fetch(healthUrl, {
          signal: controller.signal,
          headers: {
            'Cache-Control': 'no-cache'
          }
        });

        clearTimeout(timeoutId);

        console.log('🏥 [HealthCheck] Response status:', response.status);
        console.log('🏥 [HealthCheck] Response ok:', response.ok);

        if (!response.ok) {
          console.log('❌ [HealthCheck] Backend returned error');
          return { status: 'backend_down', message: 'Backend returned error' };
        }

        const data = await response.json();
        console.log('✅ [HealthCheck] Health response:', data);

        // If health endpoint works, backend is up
        // Now check database-specific endpoint
        try {
          console.log('🏥 [HealthCheck] Checking database health...');
          await api.get('/health/db');
          console.log('✅ [HealthCheck] All systems operational');
          return { status: 'healthy', message: 'All systems operational' };
        } catch (dbError: any) {
          console.log('⚠️ [HealthCheck] Database check error:', dbError);
          // Backend is up but database is down
          if (dbError.response?.status === 503) {
            return { status: 'database_down', message: 'Database unavailable' };
          }
          // If we can't determine, assume healthy since basic health passed
          return { status: 'healthy', message: 'Backend operational' };
        }
      } catch (fetchError: any) {
        clearTimeout(timeoutId);

        console.log('❌ [HealthCheck] Fetch error:', fetchError);
        console.log('❌ [HealthCheck] Error name:', fetchError.name);
        console.log('❌ [HealthCheck] Error message:', fetchError.message);

        if (fetchError.name === 'AbortError') {
          console.log('⏱️ [HealthCheck] Backend timeout (>5s)');
          return { status: 'backend_down', message: 'Backend timeout' };
        }

        return { status: 'backend_down', message: 'Cannot reach backend' };
      }
    } catch (error) {
      console.log('❌ [HealthCheck] Unexpected error:', error);
      return { status: 'network_error', message: 'Network error' };
    }
  }

  /**
   * Start monitoring health status
   * @param interval Check interval in milliseconds (default: 60000 = 1 minute)
   */
  startMonitoring(interval: number = 60000) {
    if (this.checkInterval) {
      this.stopMonitoring();
    }

    // Initial check
    this.performCheck();

    // Periodic checks
    this.checkInterval = setInterval(() => {
      this.performCheck();
    }, interval);

    // Listen to online/offline events
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }

  /**
   * Stop monitoring
   */
  stopMonitoring() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }

    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
  }

  /**
   * Subscribe to health status changes
   */
  subscribe(callback: (status: HealthStatus) => void) {
    this.listeners.add(callback);

    // Immediately notify with current status
    callback(this.lastStatus);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Get last known status
   */
  getLastStatus(): HealthStatus {
    return this.lastStatus;
  }

  private async performCheck() {
    const result = await this.checkHealth();

    // Only notify if status changed
    if (result.status !== this.lastStatus) {
      this.lastStatus = result.status;
      this.notifyListeners(result.status);
    }
  }

  private notifyListeners(status: HealthStatus) {
    this.listeners.forEach(callback => callback(status));
  }

  private handleOnline = () => {
    this.performCheck();
  };

  private handleOffline = () => {
    this.lastStatus = 'network_error';
    this.notifyListeners('network_error');
  };
}

// Export singleton instance
export const healthCheckService = new HealthCheckService();
